'use client';

import { enforceParagraphSpacing } from '@/utils/markdownSpacing';
import { editableHtmlToMarkdown } from '@/utils/editableHtmlToMarkdown';
import {
    extractInlineFontFamilies,
    getKnownGoogleFontWeights,
} from '@/utils/google-fonts';
import {
    addSoftHyphens,
    rehypeEmergencyHyphenation,
} from '@/utils/softHyphenation';
import { Marker } from '@/components/text/marker';
import clsx from 'clsx';
import { contrastColor } from 'contrast-color';
import {
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { ReactMarkdown } from 'react-markdown/lib/react-markdown';
import rehypeRaw from 'rehype-raw';
import remarkBreaks from 'remark-breaks';
import { useInlineEdit } from './InlineEditContext';

const SEMANTIC_HEADING_TAGS = new Set(['h1', 'h2', 'h3', 'h4', 'h5', 'h6']);

function headingClasses() {
    // Heading overrides are semantic only. Typography continues to inherit
    // from the component so changing H2 to H3 never changes its appearance.
    return 'whitespace-pre-wrap';
}

function omitMarkdownDomProps(props = {}) {
    const { node, ordered, index, checked, sourcePosition, ...domProps } =
        props;
    void node;
    void ordered;
    void index;
    void checked;
    void sourcePosition;
    return domProps;
}

function restoreMarkerStyle(node, style) {
    const rawStyle = node?.properties?.style;
    if (
        typeof rawStyle !== 'string' ||
        (!rawStyle.includes('--editor-marker') &&
            !rawStyle.includes('--editor-line-height'))
    ) {
        return style;
    }

    const restored = { ...(style || {}) };
    // react-markdown v8 camel-cases CSS custom properties incorrectly on the
    // client (for example --editor-marker becomes -EditorMarker). Rehydrate
    // the original names from the HAST style string before React renders it.
    delete restored['-EditorMarker'];
    delete restored['-EditorMarkerColor'];
    delete restored['-EditorMarkerGradient'];
    delete restored['-EditorMarkerAngle'];
    delete restored['-EditorMarkerDuration'];
    delete restored['-EditorMarkerDelay'];
    delete restored['-EditorMarkerSize'];
    delete restored['-EditorMarkerThickness'];
    delete restored['-EditorMarkerDotSize'];
    delete restored['-EditorMarkerRadius'];
    delete restored['-EditorMarkerOffsetY'];
    delete restored['-EditorMarkerReplay'];
    delete restored['-EditorMarkerAnimate'];
    delete restored['-EditorMarkerSvg'];
    delete restored['-EditorMarkerImage'];
    delete restored['-EditorLineHeight'];
    delete restored['-EditorMarkerLayer'];
    delete restored['-EditorMarkerDirection'];

    for (const property of [
        '--editor-marker',
        '--editor-marker-color',
        '--editor-marker-gradient',
        '--editor-marker-angle',
        '--editor-marker-duration',
        '--editor-marker-delay',
        '--editor-marker-size',
        '--editor-marker-thickness',
        '--editor-marker-dot-size',
        '--editor-marker-radius',
        '--editor-marker-offset-y',
        '--editor-marker-replay',
        '--editor-marker-animate',
        '--editor-marker-svg',
        '--editor-marker-image',
        '--editor-line-height',
        '--editor-marker-layer',
        '--editor-marker-direction',
    ]) {
        const escaped = property.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const match = rawStyle.match(
            new RegExp(`(?:^|;)\\s*${escaped}\\s*:\\s*([^;]+)`, 'i')
        );
        if (match?.[1]) restored[property] = match[1].trim();
    }

    return restored;
}

function getEmergencyHyphenContainer(element) {
    const ownerWindow = element?.ownerDocument?.defaultView;
    let current = element?.parentElement;

    while (current && current !== element.ownerDocument.body) {
        const computed = ownerWindow.getComputedStyle(current);
        if (computed.display !== 'inline' && computed.display !== 'contents') {
            const width =
                current.clientWidth -
                (parseFloat(computed.paddingLeft) || 0) -
                (parseFloat(computed.paddingRight) || 0);
            if (width > 0) return { element: current, width };
        }
        current = current.parentElement;
    }

    return null;
}

function EmergencyHyphenWord({ children }) {
    const ref = useRef(null);
    const word = String(children ?? '');
    const [mustHyphenate, setMustHyphenate] = useState(false);

    useLayoutEffect(() => {
        const element = ref.current;
        const ownerDocument = element?.ownerDocument;
        const ownerWindow = ownerDocument?.defaultView;
        if (!element || !ownerDocument || !ownerWindow) return undefined;

        let container = null;
        const measure = () => {
            container = getEmergencyHyphenContainer(element);
            if (!container) return;

            const computed = ownerWindow.getComputedStyle(element);
            const canvas = ownerDocument.createElement('canvas');
            const context = canvas.getContext('2d');
            if (!context) return;

            context.font = computed.font;
            const transformedWord =
                computed.textTransform === 'uppercase'
                    ? word.toUpperCase()
                    : computed.textTransform === 'lowercase'
                    ? word.toLowerCase()
                    : word;
            const letterSpacing = parseFloat(computed.letterSpacing) || 0;
            const intrinsicWidth =
                context.measureText(transformedWord).width +
                Math.max(0, transformedWord.length - 1) * letterSpacing;
            const next = intrinsicWidth > container.width + 0.5;
            setMustHyphenate((current) => (current === next ? current : next));
        };

        measure();
        const observer =
            typeof ownerWindow.ResizeObserver === 'function'
                ? new ownerWindow.ResizeObserver(measure)
                : null;
        if (container?.element) observer?.observe(container.element);
        ownerWindow.addEventListener('resize', measure);
        ownerDocument.fonts?.ready.then(measure).catch(() => {});

        return () => {
            observer?.disconnect();
            ownerWindow.removeEventListener('resize', measure);
        };
    }, [word]);

    return (
        <span
            ref={ref}
            data-emergency-hyphenated={mustHyphenate ? 'true' : 'false'}
            style={{
                hyphens: 'manual',
                overflowWrap: mustHyphenate ? 'anywhere' : 'normal',
                whiteSpace: mustHyphenate ? 'normal' : 'nowrap',
                wordBreak: 'normal',
            }}
        >
            {mustHyphenate ? addSoftHyphens(word, 4) : word}
        </span>
    );
}

export function MarkerSpan({ node, style, children, className, ...rest }) {
    if (node?.properties?.dataEmergencyHyphenWord != null) {
        return <EmergencyHyphenWord>{children}</EmergencyHyphenWord>;
    }

    const restoredStyle = restoreMarkerStyle(node, style);
    const variant = restoredStyle?.['--editor-marker'];
    const lineHeight = restoredStyle?.['--editor-line-height'];
    const frontendStyle = lineHeight
        ? { ...restoredStyle, lineHeight }
        : restoredStyle;

    if (!variant) {
        return (
            <span
                {...omitMarkdownDomProps(rest)}
                className={className}
                style={frontendStyle}
            >
                {children}
            </span>
        );
    }

    const durationMs = Number(restoredStyle['--editor-marker-duration'] || 700);
    const delayMs = Number(restoredStyle['--editor-marker-delay'] || 0);
    const size = Number(restoredStyle['--editor-marker-size'] || 1);
    const thickness = parseFloat(
        restoredStyle['--editor-marker-thickness'] || '2'
    );
    const dotSize = parseFloat(
        restoredStyle['--editor-marker-dot-size'] || '1.5'
    );
    const radius = parseFloat(restoredStyle['--editor-marker-radius'] || '32');
    const offsetY = parseFloat(
        restoredStyle['--editor-marker-offset-y'] || '0'
    );
    const animate = restoredStyle['--editor-marker-animate'] !== '0';

    return (
        <Marker
            {...omitMarkdownDomProps(rest)}
            animate={animate}
            className={['editor-marker-adapter', className]
                .filter(Boolean)
                .join(' ')}
            color={restoredStyle['--editor-marker-color']}
            delayMs={delayMs}
            durationMs={durationMs}
            gradient={restoredStyle['--editor-marker-gradient']}
            angle={
                restoredStyle['--editor-marker-angle'] == null
                    ? undefined
                    : Number(restoredStyle['--editor-marker-angle'])
            }
            customSvg={restoredStyle['--editor-marker-svg']}
            customImage={restoredStyle['--editor-marker-image']}
            layer={
                restoredStyle['--editor-marker-layer'] ||
                (variant === 'strikethrough' || variant === 'cross-out'
                    ? 'over'
                    : 'under')
            }
            size={size}
            thickness={thickness}
            dotSize={dotSize}
            radius={radius}
            offsetY={offsetY}
            replayKey={restoredStyle['--editor-marker-replay']}
            direction={
                restoredStyle['--editor-marker-direction'] ||
                (variant === 'highlight' ? 'left' : 'center')
            }
            style={frontendStyle}
            variant={variant}
        >
            {children}
        </Marker>
    );
}

export function getMarkdownLinkAriaLabel(href = '') {
    const value = String(href).trim();
    if (!/^tel:/i.test(value)) return undefined;

    const phone = value
        .slice(value.indexOf(':') + 1)
        .replace(/\\/g, '')
        .trim();
    return phone ? `Call ${phone}` : 'Call';
}

function getCaretRangeFromPoint(doc, x, y) {
    const position = doc.caretPositionFromPoint?.(x, y);
    if (position) {
        const range = doc.createRange();
        range.setStart(position.offsetNode, position.offset);
        range.collapse(true);
        return range;
    }

    return doc.caretRangeFromPoint?.(x, y) || null;
}

function getTextOffsetFromPoint(root, x, y) {
    const doc = root?.ownerDocument;
    if (!doc) return null;

    const pointRange = getCaretRangeFromPoint(doc, x, y);
    if (!pointRange || !root.contains(pointRange.startContainer)) return null;

    const offsetRange = doc.createRange();
    offsetRange.selectNodeContents(root);
    offsetRange.setEnd(pointRange.startContainer, pointRange.startOffset);
    return offsetRange.toString().length;
}

function getRangeAtTextOffset(root, textOffset) {
    const doc = root.ownerDocument;
    const nodeFilter = doc.defaultView?.NodeFilter;
    const range = doc.createRange();

    if (!nodeFilter || textOffset == null) {
        range.selectNodeContents(root);
        range.collapse(false);
        return range;
    }

    const walker = doc.createTreeWalker(root, nodeFilter.SHOW_TEXT);
    let remaining = Math.max(0, textOffset);
    let lastTextNode = null;
    let textNode = walker.nextNode();

    while (textNode) {
        lastTextNode = textNode;
        const length = textNode.textContent?.length || 0;
        if (remaining <= length) {
            range.setStart(textNode, remaining);
            range.collapse(true);
            return range;
        }
        remaining -= length;
        textNode = walker.nextNode();
    }

    if (lastTextNode) {
        range.setStart(lastTextNode, lastTextNode.textContent?.length || 0);
        range.collapse(true);
    } else {
        range.selectNodeContents(root);
        range.collapse(false);
    }
    return range;
}

function getSelectionTextOffsets(root) {
    const doc = root?.ownerDocument;
    const selection = doc?.defaultView?.getSelection();
    if (!root || !doc || !selection || selection.rangeCount === 0) return null;

    const range = selection.getRangeAt(0);
    if (
        selection.isCollapsed ||
        !root.contains(range.startContainer) ||
        !root.contains(range.endContainer)
    ) {
        return null;
    }

    const prefixRange = doc.createRange();
    prefixRange.selectNodeContents(root);
    prefixRange.setEnd(range.startContainer, range.startOffset);
    const start = prefixRange.toString().length;
    return { start, end: start + range.toString().length };
}

export function MarkDown(props) {
    const {
        className,
        style,
        children,
        variant,
        wrapperClassName,
        components: externalComponents,
        fieldKey: explicitFieldKey,
    } = props;

    const markdownSource = useMemo(() => {
        if (children == null) return '';
        if (typeof children !== 'string' && typeof children !== 'number')
            return '';
        const isSemanticHeading = SEMANTIC_HEADING_TAGS.has(variant);
        const applyMode = isSemanticHeading ? 'never' : 'always';
        const source = enforceParagraphSpacing(String(children), {
            apply: applyMode,
        });
        if (isSemanticHeading) {
            return source
                .replace(/\r\n?/g, '\n')
                .replace(
                    /(^|\n)[ \t\u00a0]*(?:&nbsp;)?[ \t\u00a0]*(?=\n|$)/g,
                    ''
                );
        }
        return source;
    }, [children, variant]);
    const inlineFontFamilies = useMemo(
        () => extractInlineFontFamilies(markdownSource),
        [markdownSource]
    );

    // Normal content sits on a light surface, so links should start with the
    // stronger primary color. Background detection can opt into the lighter
    // color when the content is rendered on a dark surface.
    const [linkColor, setLinkColor] = useState('link-dark');
    const ref = useRef();
    const inlineContentRef = useRef(null);
    const inlineCaretOffsetRef = useRef(null);
    const inlinePendingSelectionRef = useRef(null);
    const inlineFieldKeyRef = useRef(null);
    const inlineSelectionRangeRef = useRef(null);
    const inlineEdit = useInlineEdit();
    const isTextValue =
        typeof children === 'string' || typeof children === 'number';
    const rawInlineValue = isTextValue ? String(children) : '';
    const inlineDraftRef = useRef(rawInlineValue);
    const inlineFieldKey = useMemo(
        () =>
            explicitFieldKey ||
            (rawInlineValue && inlineEdit?.resolveTextFieldKey
                ? inlineEdit.resolveTextFieldKey(rawInlineValue)
                : null),
        [explicitFieldKey, inlineEdit, rawInlineValue]
    );
    if (inlineFieldKey) inlineFieldKeyRef.current = inlineFieldKey;
    const resolvedInlineFieldKey = isTextValue
        ? inlineFieldKey || inlineFieldKeyRef.current
        : null;
    const canInlineEdit = Boolean(
        inlineEdit?.enabled &&
            resolvedInlineFieldKey &&
            typeof inlineEdit.onInlineSave === 'function'
    );
    const [isInlineEditing, setIsInlineEditing] = useState(false);
    const [isInlineSaving, setIsInlineSaving] = useState(false);
    const [inlineFormatMenu, setInlineFormatMenu] = useState(null);

    useEffect(() => {
        if (!isInlineEditing) {
            inlineDraftRef.current = rawInlineValue;
            inlineSelectionRangeRef.current = null;
            setInlineFormatMenu(null);
        }
    }, [isInlineEditing, rawInlineValue]);

    useEffect(() => {
        if (isInlineEditing && rawInlineValue !== inlineDraftRef.current) {
            // Keep a toolbar edit made through the component panel from being
            // overwritten when the frontend inline editor subsequently closes.
            inlineDraftRef.current = rawInlineValue;
        }
    }, [isInlineEditing, rawInlineValue]);

    const updateInlineFormatMenu = useCallback(() => {
        const content = inlineContentRef.current;
        const wrapper = ref.current;
        const ownerDocument = content?.ownerDocument;
        const selection = ownerDocument?.defaultView?.getSelection();

        if (
            !content ||
            !wrapper ||
            !selection ||
            selection.rangeCount === 0 ||
            selection.isCollapsed
        ) {
            inlineSelectionRangeRef.current = null;
            setInlineFormatMenu(null);
            return;
        }

        const range = selection.getRangeAt(0);
        if (
            !content.contains(range.commonAncestorContainer) ||
            !range.toString().trim()
        ) {
            inlineSelectionRangeRef.current = null;
            setInlineFormatMenu(null);
            return;
        }

        const rangeRect = range.getBoundingClientRect();
        const wrapperRect = wrapper.getBoundingClientRect();
        const menuWidth = 20;
        const left = Math.min(
            Math.max(rangeRect.right - wrapperRect.left + 4, 0),
            Math.max(0, wrapperRect.width - menuWidth)
        );

        inlineSelectionRangeRef.current = range.cloneRange();
        const prefixRange = ownerDocument.createRange();
        prefixRange.selectNodeContents(content);
        prefixRange.setEnd(range.startContainer, range.startOffset);
        const selectionDetail = {
            fieldKey: resolvedInlineFieldKey,
            start: prefixRange.toString().length,
            text: range.toString(),
        };
        const selectionEvent = new ownerDocument.defaultView.CustomEvent(
            'editor:frontend-text-selection',
            { detail: selectionDetail }
        );
        ownerDocument.defaultView.dispatchEvent(selectionEvent);
        try {
            if (
                ownerDocument.defaultView.parent !== ownerDocument.defaultView
            ) {
                ownerDocument.defaultView.parent.dispatchEvent(
                    new ownerDocument.defaultView.parent.CustomEvent(
                        'editor:frontend-text-selection',
                        { detail: selectionDetail }
                    )
                );
            }
        } catch {
            // A cross-origin preview cannot forward its native selection.
        }
        setInlineFormatMenu((current) => ({
            left,
            top:
                rangeRect.top -
                wrapperRect.top +
                Math.max(rangeRect.height, 20) / 2,
            expanded: current?.expanded || false,
            bold: ownerDocument.queryCommandState?.('bold') || false,
            italic: ownerDocument.queryCommandState?.('italic') || false,
            underline: ownerDocument.queryCommandState?.('underline') || false,
        }));
    }, [resolvedInlineFieldKey]);

    const applyInlineFormat = useCallback(
        (command) => {
            const content = inlineContentRef.current;
            const ownerDocument = content?.ownerDocument;
            const selection = ownerDocument?.defaultView?.getSelection();
            const range = inlineSelectionRangeRef.current;
            if (!content || !ownerDocument || !selection || !range) return;

            selection.removeAllRanges();
            selection.addRange(range);
            ownerDocument.execCommand(command, false);
            inlineDraftRef.current = editableHtmlToMarkdown(content);
            updateInlineFormatMenu();
        },
        [updateInlineFormatMenu]
    );

    useEffect(() => {
        if (!isInlineEditing) return undefined;

        const content = inlineContentRef.current;
        const ownerWindow = content?.ownerDocument?.defaultView;
        if (!content || !ownerWindow) return undefined;

        const frameId = ownerWindow.requestAnimationFrame(() => {
            content.focus();
            const pendingSelection = inlinePendingSelectionRef.current;
            const range = getRangeAtTextOffset(
                content,
                pendingSelection?.start ?? inlineCaretOffsetRef.current
            );
            if (
                pendingSelection &&
                pendingSelection.end > pendingSelection.start
            ) {
                const endRange = getRangeAtTextOffset(
                    content,
                    pendingSelection.end
                );
                range.setEnd(endRange.endContainer, endRange.endOffset);
            }
            inlineCaretOffsetRef.current = null;
            inlinePendingSelectionRef.current = null;

            const selection = ownerWindow.getSelection();
            selection?.removeAllRanges();
            selection?.addRange(range);
            if (!range.collapsed) updateInlineFormatMenu();
        });
        return () => ownerWindow.cancelAnimationFrame(frameId);
    }, [isInlineEditing, updateInlineFormatMenu]);

    const startInlineEdit = () => {
        inlineDraftRef.current = rawInlineValue;
        setIsInlineEditing(true);
    };

    const cancelInlineEdit = () => {
        if (isInlineSaving) return;
        inlineDraftRef.current = rawInlineValue;
        setIsInlineEditing(false);
    };

    const saveInlineEdit = useCallback(
        async ({ closeImmediately = false } = {}) => {
            if (!resolvedInlineFieldKey || isInlineSaving) return;

            setIsInlineSaving(true);
            try {
                const saveRequest = inlineEdit.onInlineSave({
                    fieldKey: resolvedInlineFieldKey,
                    value: inlineDraftRef.current,
                });
                if (closeImmediately) setIsInlineEditing(false);

                const saved = await saveRequest;
                if (saved) setIsInlineEditing(false);
                else if (closeImmediately) setIsInlineEditing(true);
                return saved;
            } finally {
                setIsInlineSaving(false);
            }
        },
        [inlineEdit, isInlineSaving, resolvedInlineFieldKey]
    );

    useEffect(() => {
        if (!isInlineEditing || isInlineSaving) return undefined;

        const ownerDocument = ref.current?.ownerDocument;
        if (!ownerDocument) return undefined;

        const handleOutsidePointerDown = (event) => {
            if (ref.current?.contains(event.target)) return;

            const bounds = ref.current?.getBoundingClientRect();
            const dashedInset = 4;
            const isInsideDashedArea =
                bounds &&
                event.clientX >= bounds.left - dashedInset &&
                event.clientX <= bounds.right + dashedInset &&
                event.clientY >= bounds.top - dashedInset &&
                event.clientY <= bounds.bottom + dashedInset;

            if (isInsideDashedArea) return;
            if (inlineDraftRef.current === rawInlineValue) {
                setIsInlineEditing(false);
                return;
            }
            saveInlineEdit({ closeImmediately: true });
        };

        ownerDocument.addEventListener(
            'pointerdown',
            handleOutsidePointerDown,
            true
        );
        return () =>
            ownerDocument.removeEventListener(
                'pointerdown',
                handleOutsidePointerDown,
                true
            );
    }, [isInlineEditing, isInlineSaving, rawInlineValue, saveInlineEdit]);

    useEffect(() => {
        if (!ref.current) return;
        const ownerDocument = ref.current.ownerDocument;
        const ownerWindow = ownerDocument.defaultView;
        if (!ownerWindow) return;

        let element = ref.current;
        let depth = 0;
        while (element && depth++ < 10) {
            const bgColor = ownerWindow
                .getComputedStyle(element)
                .getPropertyValue('background-color');
            if (bgColor === 'rgba(0, 0, 0, 0)' || bgColor === 'transparent') {
                element = element.parentElement;
            } else {
                const rootStyle = ownerWindow.getComputedStyle(
                    ownerDocument.documentElement
                );
                const light = rootStyle.getPropertyValue('--primary-400');
                const dark = rootStyle.getPropertyValue('--primary-700');
                const result = contrastColor({
                    bgColor: rgbToHex(bgColor),
                    fgLightColor: light,
                    fgDarkColor: dark,
                });
                setLinkColor(result === dark ? 'link-dark' : 'link-light');
                break;
            }
        }
    }, [ref.current]);

    useEffect(() => {
        const doc = ref.current?.ownerDocument;
        if (!doc?.head || !inlineFontFamilies.length) return;

        const loadedFamilies = new Set(
            Array.from(
                doc.head.querySelectorAll('link[data-markdown-font-family]')
            ).map((link) => link.dataset.markdownFontFamily)
        );

        inlineFontFamilies.forEach((family) => {
            const key = family.toLowerCase();
            if (loadedFamilies.has(key)) return;

            const weights = getKnownGoogleFontWeights(family);
            if (!weights.length) return;

            const familyParam = encodeURIComponent(family).replace(/%20/g, '+');
            const link = doc.createElement('link');
            link.rel = 'stylesheet';
            link.href = `https://fonts.googleapis.com/css2?family=${familyParam}:wght@${weights.join(
                ';'
            )}&display=swap`;
            link.dataset.markdownFontFamily = key;
            doc.head.appendChild(link);
            loadedFamilies.add(key);
        });
    }, [inlineFontFamilies]);

    const markdownComponents = useMemo(() => {
        const Paragraph = SEMANTIC_HEADING_TAGS.has(variant)
            ? ({ className, children, ...rest }) => {
                  const Element = variant; // 'h1'...'h6'
                  return (
                      <Element
                          {...omitMarkdownDomProps(rest)}
                          style={style}
                          className={clsx(
                              className,
                              'leading-[1.08] [text-wrap:wrap]'
                          )}
                      >
                          {children}
                      </Element>
                  );
              }
            : ({ className, children, ...rest }) => (
                  <p
                      {...omitMarkdownDomProps(rest)}
                      className={clsx(
                          className,
                          'mb-4 whitespace-pre-wrap last:mb-0'
                      )}
                  >
                      {children}
                  </p>
              );

        const isExternal = (href = '') => /^(?:https?:)?\/\//i.test(href);

        return {
            span: MarkerSpan,
            p: Paragraph,
            h1: ({ className, children, ...rest }) => (
                <h1
                    {...omitMarkdownDomProps(rest)}
                    className={clsx(className, headingClasses('h1'))}
                >
                    {children}
                </h1>
            ),
            h2: ({ className, children, ...rest }) => (
                <h2
                    {...omitMarkdownDomProps(rest)}
                    className={clsx(className, headingClasses('h2'))}
                >
                    {children}
                </h2>
            ),
            h3: ({ className, children, ...rest }) => (
                <h3
                    {...omitMarkdownDomProps(rest)}
                    className={clsx(className, headingClasses('h3'))}
                >
                    {children}
                </h3>
            ),
            h4: ({ className, children, ...rest }) => (
                <h4
                    {...omitMarkdownDomProps(rest)}
                    className={clsx(className, headingClasses('h4'))}
                >
                    {children}
                </h4>
            ),
            h5: ({ className, children, ...rest }) => (
                <h5
                    {...omitMarkdownDomProps(rest)}
                    className={clsx(className, headingClasses('h5'))}
                >
                    {children}
                </h5>
            ),
            h6: ({ className, children, ...rest }) => (
                <h6
                    {...omitMarkdownDomProps(rest)}
                    className={clsx(className, headingClasses('h6'))}
                >
                    {children}
                </h6>
            ),
            ul: ({ className, children, ...rest }) => (
                <ul
                    {...omitMarkdownDomProps(rest)}
                    className={clsx(
                        className,
                        'my-4 list-disc space-y-1 pl-6 marker:text-current'
                    )}
                >
                    {children}
                </ul>
            ),
            ol: ({ className, children, ...rest }) => (
                <ol
                    {...omitMarkdownDomProps(rest)}
                    className={clsx(
                        className,
                        'my-4 list-decimal space-y-1 pl-6 marker:text-current'
                    )}
                >
                    {children}
                </ol>
            ),
            li: ({ className, children, ...rest }) => (
                <li
                    {...omitMarkdownDomProps(rest)}
                    className={clsx(className, 'pl-1 [&>p]:mb-0')}
                >
                    {children}
                </li>
            ),

            a: ({ href, title, children, className, ...rest }) => {
                const openNew = title === 'newtab' || isExternal(href);
                const target = openNew ? '_blank' : undefined;
                const rel = openNew ? 'noopener noreferrer' : undefined;
                const ariaLabel = getMarkdownLinkAriaLabel(href);

                return (
                    <a
                        {...omitMarkdownDomProps(rest)}
                        href={href}
                        title={title}
                        target={target}
                        rel={rel}
                        aria-label={ariaLabel}
                        className={clsx(className, linkColor)}
                    >
                        {children}
                    </a>
                );
            },
            ...externalComponents,
        };
    }, [variant, linkColor, externalComponents, style]);

    return (
        <div
            ref={ref}
            data-markdown-field-key={resolvedInlineFieldKey || undefined}
            className={clsx(
                wrapperClassName,
                canInlineEdit && 'group/inline-edit relative cursor-text'
            )}
            onClick={(event) => {
                if (!canInlineEdit || isInlineEditing) return;
                inlinePendingSelectionRef.current = getSelectionTextOffsets(
                    ref.current
                );
                event.preventDefault();
                event.stopPropagation();
                if (!inlinePendingSelectionRef.current) {
                    inlineCaretOffsetRef.current = getTextOffsetFromPoint(
                        ref.current,
                        event.clientX,
                        event.clientY
                    );
                }
                startInlineEdit();
            }}
        >
            {canInlineEdit && (
                <span
                    aria-hidden="true"
                    className={clsx(
                        'border-primary-500/70 pointer-events-none absolute -inset-1 z-0 rounded-md border-2 border-dashed transition-opacity',
                        isInlineEditing
                            ? 'bg-white/[0.03] opacity-100'
                            : 'opacity-0 group-focus-within/inline-edit:opacity-100 group-hover/inline-edit:opacity-100'
                    )}
                />
            )}
            {isInlineEditing ? (
                <>
                    <div
                        ref={inlineContentRef}
                        contentEditable
                        suppressContentEditableWarning
                        role="textbox"
                        aria-label={`Edit ${
                            inlineEdit.componentName || 'text'
                        }`}
                        className={clsx(
                            className,
                            'relative z-10 cursor-text outline-none'
                        )}
                        onInput={(event) => {
                            inlineDraftRef.current = editableHtmlToMarkdown(
                                event.currentTarget
                            );
                        }}
                        onPointerUp={() => {
                            requestAnimationFrame(updateInlineFormatMenu);
                        }}
                        onKeyUp={(event) => {
                            if (
                                event.shiftKey ||
                                event.key.startsWith('Arrow')
                            ) {
                                requestAnimationFrame(updateInlineFormatMenu);
                            }
                        }}
                        onKeyDown={(event) => {
                            if (
                                event.key === 'Enter' &&
                                (event.metaKey || event.ctrlKey)
                            ) {
                                event.preventDefault();
                                saveInlineEdit();
                            }
                            if (event.key === 'Escape') {
                                event.preventDefault();
                                cancelInlineEdit();
                            }
                        }}
                    >
                        <ReactMarkdown
                            rehypePlugins={[rehypeRaw]}
                            remarkPlugins={[remarkBreaks]}
                            components={markdownComponents}
                        >
                            {markdownSource}
                        </ReactMarkdown>
                    </div>
                    {inlineFormatMenu && (
                        <div
                            className="absolute z-30 flex -translate-y-1/2 items-center rounded-md border border-white/10 bg-neutral-950/90 p-0.5 text-white shadow-lg backdrop-blur-sm"
                            style={{
                                left: inlineFormatMenu.left,
                                top: inlineFormatMenu.top,
                            }}
                            role="toolbar"
                            aria-label="Format selected text"
                            onMouseDown={(event) => event.preventDefault()}
                        >
                            {inlineFormatMenu.expanded ? (
                                <>
                                    {[
                                        ['bold', 'B', 'Bold'],
                                        ['italic', 'I', 'Italic'],
                                        ['underline', 'U', 'Underline'],
                                    ].map(([command, label, title]) => (
                                        <button
                                            key={command}
                                            type="button"
                                            className={clsx(
                                                'flex h-5 w-5 items-center justify-center rounded text-[9px] text-white transition-colors hover:bg-white/20',
                                                command === 'bold' &&
                                                    'font-bold',
                                                command === 'italic' &&
                                                    'italic',
                                                command === 'underline' &&
                                                    'underline',
                                                inlineFormatMenu[command] &&
                                                    'bg-primary-600'
                                            )}
                                            title={title}
                                            aria-label={title}
                                            aria-pressed={Boolean(
                                                inlineFormatMenu[command]
                                            )}
                                            onClick={() =>
                                                applyInlineFormat(command)
                                            }
                                        >
                                            {label}
                                        </button>
                                    ))}
                                </>
                            ) : (
                                <button
                                    type="button"
                                    className="flex h-5 w-5 items-center justify-center rounded text-[8px] font-semibold text-white transition-colors hover:bg-white/20"
                                    title="Format selected text"
                                    aria-label="Format selected text"
                                    onClick={() =>
                                        setInlineFormatMenu((current) =>
                                            current
                                                ? {
                                                      ...current,
                                                      expanded: true,
                                                  }
                                                : current
                                        )
                                    }
                                >
                                    Aa
                                </button>
                            )}
                        </div>
                    )}
                </>
            ) : (
                <ReactMarkdown
                    rehypePlugins={[rehypeRaw, rehypeEmergencyHyphenation]}
                    remarkPlugins={[remarkBreaks]}
                    components={markdownComponents}
                    className={clsx(
                        className,
                        canInlineEdit && 'relative z-10'
                    )}
                >
                    {markdownSource}
                </ReactMarkdown>
            )}
        </div>
    );
}

function rgbToHex(r, g, b) {
    // 1. Convert arguments to numbers. If they are undefined, null, or NaN,
    //    Number() will result in 0 or NaN. The `|| 0` catches all of them.
    const safeR = Number(r) || 0;
    const safeG = Number(g) || 0;
    const safeB = Number(b) || 0;

    // 2. Helper function to convert a number to a 2-digit hex string
    const toHex = (c) => {
        // Clamp the value between 0 and 255
        const clamped = Math.max(0, Math.min(c, 255));
        const hex = clamped.toString(16);
        // Pad with a '0' if it's only one digit (e.g., "A" -> "0A")
        return hex.length === 1 ? '0' + hex : hex;
    };

    return `#${toHex(safeR)}${toHex(safeG)}${toHex(safeB)}`;
}
